import { addDays, differenceInCalendarDays, isValid, parseISO, startOfDay } from 'date-fns'
import type { Proposta } from '@/types'
import type { BadgeVariant } from '@/components/ui/Badge'
import { formatDate } from '@/utils/format-date'

export type WizardStep = 1 | 2 | 3

export type PropostaValidityInfo = {
  expiresAt: Date
  expiresAtLabel: string
  daysRemaining: number
}

export function getPropostaValidityInfo(proposta: {
  createdAt?: string
  validade: number
  validUntil?: string | Date | null
}): PropostaValidityInfo {
  const validadeDays = Math.max(1, Number(proposta.validade) || 7)
  let expiresAt: Date

  if (proposta.validUntil) {
    expiresAt =
      proposta.validUntil instanceof Date ? proposta.validUntil : parseISO(String(proposta.validUntil))
  } else if (proposta.createdAt) {
    const createdAt = parseISO(proposta.createdAt)
    expiresAt = isValid(createdAt) ? addDays(createdAt, validadeDays) : addDays(new Date(), validadeDays)
  } else {
    expiresAt = addDays(new Date(), validadeDays)
  }

  if (!isValid(expiresAt)) {
    expiresAt = addDays(new Date(), validadeDays)
  }

  const daysRemaining = Math.max(0, differenceInCalendarDays(startOfDay(expiresAt), startOfDay(new Date())))

  return {
    expiresAt,
    expiresAtLabel: formatDate(expiresAt),
    daysRemaining,
  }
}

export type CustomerData = {
  nome: string
  email: string
  telefone: string
  empresa: string
  cargo: string
  cnpj: string
  responsavelFinanceiroNome: string
  emailFinanceiro: string
}

export function isValidCustomerEmail(value: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim())
}

export type PublicCadastroPayload = {
  signerName?: string
  signerEmail?: string
  cnpj?: string
  empresaNome?: string
  responsavelFinanceiroNome?: string
  emailFinanceiro?: string
}

/** Só envia o que já está válido. E-mail pela metade não vai no autosave. */
export function buildPublicCadastroPayload(customer: CustomerData): PublicCadastroPayload | null {
  const payload: PublicCadastroPayload = {}
  const nome = customer.nome.trim()
  if (nome.length >= 2) payload.signerName = nome
  if (isValidCustomerEmail(customer.email)) payload.signerEmail = customer.email.trim()
  const cnpj = customer.cnpj.replace(/\D/g, '')
  if (cnpj.length === 14) payload.cnpj = cnpj
  const empresa = customer.empresa.trim()
  if (empresa.length >= 2) payload.empresaNome = empresa
  const financeiro = customer.responsavelFinanceiroNome.trim()
  if (financeiro.length >= 2) payload.responsavelFinanceiroNome = financeiro
  if (isValidCustomerEmail(customer.emailFinanceiro)) payload.emailFinanceiro = customer.emailFinanceiro.trim()
  return Object.keys(payload).length > 0 ? payload : null
}

export function isPublicContractReady(customer: CustomerData): boolean {
  const cnpjDigits = customer.cnpj.replace(/\D/g, '')
  return (
    customer.nome.trim().length >= 2 &&
    isValidCustomerEmail(customer.email) &&
    cnpjDigits.length === 14 &&
    customer.responsavelFinanceiroNome.trim().length >= 2 &&
    isValidCustomerEmail(customer.emailFinanceiro)
  )
}

export const PROPOSTA_STATUS_CONFIG: Record<
  Proposta['status'],
  { label: string; variant: BadgeVariant }
> = {
  rascunho: { label: 'Rascunho', variant: 'warning' },
  enviada: { label: 'Enviada', variant: 'info' },
  aceita: { label: 'Aceita', variant: 'success' },
  rejeitada: { label: 'Rejeitada', variant: 'danger' },
  recusada: { label: 'Recusada', variant: 'danger' },
  expirada: { label: 'Expirada', variant: 'default' },
}

export function safeParseJson(value: unknown): Record<string, unknown> {
  if (!value) return {}
  if (typeof value === 'object') return value as unknown as Record<string, unknown>
  if (typeof value !== 'string') return {}
  try {
    const parsed = JSON.parse(value)
    return parsed && typeof parsed === 'object' ? parsed : {}
  } catch {
    return {}
  }
}

export function extractProposalPayload(responseData: unknown): Record<string, unknown> {
  const data = responseData as unknown as Record<string, unknown>
  const inner = data?.data as unknown as Record<string, unknown> | undefined
  return (
    (inner?.proposal as unknown as Record<string, unknown>) ??
    (data?.proposal as unknown as Record<string, unknown>) ??
    (inner?.proposta as unknown as Record<string, unknown>) ??
    (data?.proposta as unknown as Record<string, unknown>) ??
    inner ??
    data ??
    {}
  )
}

export function normalizeProposta(data: unknown): Proposta {
  const raw = extractProposalPayload(data) || {}
  const parsedDados = safeParseJson(raw.dados)

  const rawItems =
    Array.isArray(raw.items) && raw.items.length > 0
      ? raw.items
      : Array.isArray(raw.itens)
        ? raw.itens
        : []
  const parsedItems =
    Array.isArray(parsedDados.items) && parsedDados.items.length > 0
      ? parsedDados.items
      : Array.isArray(parsedDados.itens)
        ? parsedDados.itens
        : []

  const items = (rawItems.length > 0 ? rawItems : parsedItems).map((item: Record<string, unknown>) => ({
    ...item,
    quantidade: Number(item.quantidade ?? 1),
    valorUnitario: Number(item.valorUnitario ?? item.valor ?? 0),
    total: Number(
      item.total ??
        Number(item.valorUnitario ?? item.valor ?? 0) * Number(item.quantidade ?? 1),
    ),
  }))

  const status = String(raw.status ?? parsedDados.status ?? 'rascunho').toLowerCase()
  const normalizedStatus: Proposta['status'] =
    status === 'revisao'
      ? 'rascunho'
      : status === 'rejeitada'
        ? 'rejeitada'
        : status === 'recusada'
          ? 'recusada'
          : status === 'aceita'
            ? 'aceita'
            : status === 'enviada'
              ? 'enviada'
              : status === 'expirada'
                ? 'expirada'
                : 'rascunho'

  const subtotal = Number(
    raw.subtotal ??
      parsedDados.subtotal ??
      items.reduce((sum: number, item: Record<string, unknown>) => sum + Number(item.total ?? 0), 0),
  )
  const desconto = Number(raw.desconto ?? parsedDados.desconto ?? 0)
  const totalFinal = Number(
    raw.totalFinal ??
      parsedDados.totalFinal ??
      raw.valor ??
      parsedDados.valor ??
      subtotal - subtotal * (desconto / 100),
  )
  const validade = Number(raw.validade ?? parsedDados.validade ?? 7)
  const templateType = raw.templateType ?? parsedDados.templateType
  const validUntilRaw = raw.validUntil ?? parsedDados.validUntil

  return {
    ...raw,
    ...parsedDados,
    status: normalizedStatus,
    itens: items,
    items,
    combos: raw.combos ?? parsedDados.combos ?? [],
    subtotal,
    desconto,
    totalFinal,
    valor: Number(raw.valor ?? parsedDados.valor ?? totalFinal),
    validade,
    validUntil: validUntilRaw ? String(validUntilRaw) : undefined,
    observacoes: String(raw.observacoes ?? parsedDados.observacoes ?? ''),
    templateType,
    linkPublico: String(raw.linkPublico ?? raw.link ?? parsedDados.linkPublico ?? parsedDados.link ?? ''),
    link: String(raw.link ?? parsedDados.link ?? ''),
    dados: typeof raw.dados === 'string' ? raw.dados : JSON.stringify(parsedDados),
  } as unknown as Proposta
}
